import React, { useEffect, useState } from 'react';
import './Login.css';
import { auth } from '../../firebase/config/config';
import logger from '../../utils/logger';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Mail, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";

function Login() {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.body.classList.add('login-page');
    return () => {
      document.body.classList.remove('login-page');
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      if (isLoginMode) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        logger.log('Login successful!');

        // Bypass subscription check on staging domain
        const isStaging = window.location.hostname === 'staging.smushlabs.ai';
        if (!isStaging) {
          // Check if user has an active subscription
          const token = await userCredential.user.getIdToken();
          const response = await fetch('/api/subscription/status', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          // If no active subscription, redirect to get-started
          if (!data.subscription || data.subscription.status !== 'active') {
            window.location.href = '/get-started';
            return;
          }
        }
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
        logger.log('Signup successful!');
      }
      // Optionally redirect or update UI after success
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    const provider = new GoogleAuthProvider();
    try {
      const userCredential = await signInWithPopup(auth, provider);
      logger.log('Google sign-in successful!');

      // Bypass subscription check on staging domain
      const isStaging = window.location.hostname === 'staging.smushlabs.ai';
      if (!isStaging) {
        // Check if user has an active subscription
        const token = await userCredential.user.getIdToken();
        const response = await fetch('/api/subscription/status', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        // If no active subscription, redirect to get-started
        if (!data.subscription || data.subscription.status !== 'active') {
          window.location.href = '/get-started';
          return;
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleMode = () => {
    setIsLoginMode(prev => !prev);
    setError(null);
  };

  return (
    <div className="login-page">
      <div className="embossed-text">SMUSHLABS</div>
      <div className="logo-container">
        <img src="/favicon.ico" alt="SmushLabs Logo" className="logo" />
      </div>
      <div className="login-selection-page">
        <div className="login-selection-header">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.href = '/'}
            className="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          <h1 className="login-selection-title">
            {isLoginMode ? 'Welcome Back!' : 'Create an Account'}
          </h1>
        </div>

        <div className="login-form-card">
          <div className="login-form-header">
            <p className="login-form-subtitle">
              {isLoginMode
                ? 'Log in to access your account and get started.'
                : 'Sign up to create your account and start using the platform.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form-content">
            <div className="input-row">
              <Mail className="input-icon" />
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Email address"
                className="unstyled-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="input-row">
              <Lock className="input-icon" />
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                className="unstyled-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="input-toggle" onClick={() => setShowPassword(v => !v)} aria-label="Toggle password visibility">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && <p className="error-message">{error}</p>}

            <Button
              type="submit"
              className="w-full login-submit"
            >
              {isLoginMode ? 'Log In' : 'Sign Up'}
            </Button>

            {isLoginMode && (
              <>
                <Button
                  type="button"
                  className="w-full social-login-button"
                  variant="outline"
                  onClick={handleGoogleLogin}
                >
                  <img
                    src="https://cdn4.iconfinder.com/data/icons/new-google-logo-2015/400/new-google-favicon-1024.png"
                    alt="Google"
                    className="icon"
                  />
                  Sign in with Google
                </Button>
                <a href="/forgot-password" className="forgot-password-link">
                  Forgot Password?
                </a>
              </>
            )}

            <div className="login-alt-signin">
              <span className="login-form-muted">
                {isLoginMode ? "Don't have an account?" : "Already have an account?"}
              </span>{' '}
              {isLoginMode ? (
                <a href="/get-started" className="login-form-link">Sign Up</a>
              ) : (
                <button type="button" onClick={toggleMode} className="login-form-link">Log In</button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
